/**
 * Protocol Stack Grouping Preference Utility
 * Manages persisting and retrieving the user's preferred protocol card grouping.
 */

export const GROUP_BY_OPTIONS = [
  { value: 'none', label: 'Group: Off' },
  { value: 'category', label: 'Group: Compound Type' },
  { value: 'healthCategory', label: 'Group: Health Goal' },
  { value: 'timeOfDay', label: 'Group: Time of Day' }
];

export const VALID_GROUP_BY_VALUES = ['none', 'category', 'healthCategory', 'timeOfDay'];

/**
 * Returns group by options sorted alphabetically by label,
 * keeping 'Group: Off' (value: 'none') as the first option.
 */
export function getSortedGroupByOptions() {
  const offOption = GROUP_BY_OPTIONS.find(opt => opt.value === 'none');
  const otherOptions = GROUP_BY_OPTIONS
    .filter(opt => opt.value !== 'none')
    .slice()
    .sort((a, b) => a.label.localeCompare(b.label));
  return offOption ? [offOption, ...otherOptions] : otherOptions;
}

export const STACK_GROUP_BY_KEY = 'supps_peps_stack_group_by';

/**
 * Returns a safe storage key namespaced by userId if present, otherwise base key.
 */
export function getGroupStorageKey(userId) {
  if (!userId) return STACK_GROUP_BY_KEY;
  const safeId = String(userId).replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${STACK_GROUP_BY_KEY}_${safeId}`;
}

/**
 * Retrieves the persisted groupBy preference.
 * Defaults to 'none' if unset or invalid.
 */
export function getStoredGroupBy(userId) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return 'none';
    }

    // 1. Check user-namespaced key
    if (userId) {
      const userKey = getGroupStorageKey(userId);
      const userVal = window.localStorage.getItem(userKey);
      if (userVal && VALID_GROUP_BY_VALUES.includes(userVal)) {
        return userVal;
      }
    }

    // 2. Fall back to guest / base key
    const baseVal = window.localStorage.getItem(STACK_GROUP_BY_KEY);
    if (baseVal && VALID_GROUP_BY_VALUES.includes(baseVal)) {
      return baseVal;
    }
  } catch (err) {
    console.warn('Failed to load group preference from localStorage:', err);
  }
  return 'none';
}

/**
 * Persists the user's groupBy preference into localStorage.
 */
export function saveStoredGroupBy(userId, value) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (!VALID_GROUP_BY_VALUES.includes(value)) return;

    if (userId) {
      const userKey = getGroupStorageKey(userId);
      window.localStorage.setItem(userKey, value);
    } else {
      window.localStorage.setItem(STACK_GROUP_BY_KEY, value);
    }
  } catch (err) {
    console.warn('Failed to save group preference to localStorage:', err);
  }
}
